import _ from "lodash";

import { ProposalWithOwner } from "lib/frontend/cm/utils/types";
import { Proposal, Review } from "prisma/cm/client";

import { isValidProposedNumberValue } from "./common";
import { BonusValue } from "./types";
import { decimalsForProposal } from "../compensation";
import { getConvertedCurrency } from "../currency/getConvertedCurrency";
import { ReviewExchangeRates } from "../review/types";

type ProposalLikeWithBonus = Pick<Proposal, "bonus">;

export function hasEmptyProposedBonusValue<
  T extends ProposalLikeWithBonus & Pick<Proposal, "eligibleForBonus">,
>(proposal: T, review: Review) {
  const proposedBonus = getProposedBonus(proposal);
  return (
    review.enableBonus && proposal.eligibleForBonus && !isValidProposedNumberValue(proposedBonus)
  );
}

export function getLogicComputedBonus<T extends ProposalLikeWithBonus>(proposal: T) {
  return proposal.bonus?.logicComputedValue;
}

export function getBonusOverride<T extends ProposalLikeWithBonus>(proposal: T) {
  return proposal.bonus?.overrideValue ?? null;
}

export function getProposedBonus<T extends ProposalLikeWithBonus>(proposal: T) {
  return proposal.bonus?.actualValue ?? null;
}

export function getSuggestedBonus<T extends ProposalLikeWithBonus>(proposal: T) {
  const override = getBonusOverride(proposal);
  if (override !== null) {
    return override;
  }

  return getLogicComputedBonus(proposal) ?? null;
}

export function getFormattedSuggestedBonusForInput<T extends ProposalWithOwner>({
  proposal,
  targetCurrency,
  sourceCurrency,
  exchangeRates,
}: {
  proposal: T;
  targetCurrency: string;
  sourceCurrency: string;
  exchangeRates: ReviewExchangeRates;
}): { raw: number | null; formatted: string; tooltip?: string } | null {
  const suggested = getSuggestedBonus(proposal);

  if (suggested === null) return null;

  const converted = getConvertedCurrency({
    value: suggested,
    options: {
      sourceCurrency: sourceCurrency,
      targetCurrency,
      exchangeRates,
      decimals: decimalsForProposal({ proposal }),
    },
  });

  let formattedSuggestionString = converted.formatted;
  if (proposal.bonus?.formattedSuggestion && _.isNil(getBonusOverride(proposal))) {
    formattedSuggestionString = proposal.bonus?.formattedSuggestion;
  }

  return {
    raw: _.isNumber(suggested) ? suggested / 100 : null,
    formatted: formattedSuggestionString,
    tooltip: getSuggestedBonusNote(proposal)?.join("\n"),
  };
}

export function getSuggestedBonusNote<T extends ProposalLikeWithBonus>(
  proposal: T,
): string[] | null {
  const overrideNote = proposal.bonus.overrideNote;
  if (overrideNote) {
    return [overrideNote];
  }

  return proposal.bonus?.computedNote ?? null;
}

export function getSumSuggestedBonus(
  proposals: ProposalWithOwner[],
  {
    targetCurrency,
    exchangeRates,
  }: {
    targetCurrency: string;
    exchangeRates: ReviewExchangeRates;
  },
) {
  return _.sumBy(proposals, (p) => {
    const suggested = getSuggestedBonus(p);

    if (suggested === null) return 0;

    const converted = getConvertedCurrency({
      value: suggested,
      options: {
        sourceCurrency: p.owner.salaryCurrencyCode,
        targetCurrency,
        exchangeRates,
      },
    });

    return converted.raw;
  });
}

export function hasBonusBeenManuallyEdited<T extends ProposalLikeWithBonus>(proposal: T) {
  return proposal.bonus?.isManuallyEdited;
}

export function updateBonusPiece<T extends ProposalLikeWithBonus, K extends keyof BonusValue>({
  proposal,
  newValue,
  key,
}: {
  proposal: T;
  newValue: BonusValue[K];
  key: K;
}): T {
  proposal.bonus[key] = newValue;
  return proposal;
}

export function updateProposedBonus<T extends ProposalLikeWithBonus>({
  proposal,
  newValue,
  manuallyEntered = false,
}: {
  proposal: T;
  newValue: number | null;
  manuallyEntered?: boolean;
}): T {
  let updatedProposal = updateBonusPiece({
    proposal,
    newValue,
    key: "actualValue",
  });

  if (manuallyEntered) {
    updatedProposal = updateBonusPiece({
      proposal: updatedProposal,
      newValue: true,
      key: "isManuallyEdited",
    });
  }

  return updatedProposal;
}
